import { debounce, requestIdleCallback, viewTransition } from '@theme/utilities';
import { Component } from '@theme/component';

const OFFSET = 40;

/**
 * A custom element that manages a floating panel.
 */
export class FloatingPanelComponent extends Component {
  #updatePosition = async () => {
    // Wait for any view transitions to finish
    if (viewTransition.current) await viewTransition.current;

    // 232.0 SORT-CUT: in RTL the panel sits on `right: 0`, so a `left` write was dropped (over-constrained) and the sort list stayed cut
    // at the window's left edge. Move it by its inline-start side (rtl: right), cleared before measuring, and write the drawn overflow in
    // the panel's own px (divided by its zoom - the site zoom layer).
    const rtl = getComputedStyle(this).direction === 'rtl';
    const side = rtl ? 'right' : 'left';
    this.style.removeProperty(side);
    const rect = this.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const zoom = this.currentCSSZoom || 1;

    this.style.top = OFFSET + 'px';

    if (rect.right > viewportWidth) {
      const overflowAmount = (rect.right - viewportWidth + OFFSET) / zoom;
      this.style[side] = rtl ? `${overflowAmount}px` : `-${overflowAmount}px`;
    }

    if (rect.left < 0) {
      const overflowAmount = (Math.abs(rect.left) + OFFSET) / zoom;
      this.style[side] = rtl ? `-${overflowAmount}px` : `${overflowAmount}px`;
    }

    this.#mutationObserver.takeRecords();
  };

  #mutationObserver = new MutationObserver(this.#updatePosition);

  #resizeListener = debounce(() => {
    const parent = this.closest('details');
    const closeOnResize = this.dataset.closeOnResize === 'true';
    if (parent instanceof HTMLDetailsElement && closeOnResize) {
      parent.open = false;
      parent.removeAttribute('open');
      this.#updatePosition();
    }
  }, 100);

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('resize', this.#resizeListener);

    requestIdleCallback(() => {
      this.#updatePosition();
      this.#mutationObserver.observe(this, { attributes: true });
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    this.#mutationObserver.disconnect();
  }
}

if (!customElements.get('floating-panel-component')) {
  customElements.define('floating-panel-component', FloatingPanelComponent);
}
